import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Pill,
  SectionHead,
  StatusBar,
  StatusLabel,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { StatTile } from '../components/StatTile';
import { formatCents } from '../lib/format';
import { useRevenueByTools, useTools } from '../lib/queries';

export function ToolsList() {
  const { data: tools, error } = useTools();
  const ids = useMemo(() => tools?.map((t) => t.id) ?? [], [tools]);
  const revenue = useRevenueByTools(ids);

  const stats = useMemo(() => {
    const list = tools ?? [];
    const live = list.filter((t) => t.status === 'live').length;
    const draft = list.filter((t) => t.status === 'draft').length;
    const paused = list.filter((t) => t.status === 'paused').length;
    const totals = revenue.reduce<{ total: number; by: Record<string, number> }>(
      (acc, q) => {
        if (q.data) {
          acc.total += q.data.total_cents;
          for (const [k, v] of Object.entries(q.data.by_source)) {
            acc.by[k] = (acc.by[k] ?? 0) + v;
          }
        }
        return acc;
      },
      { total: 0, by: {} },
    );
    return {
      total: list.length,
      live,
      draft,
      paused,
      revenueTotal: totals.total,
      bySource: totals.by,
    };
  }, [tools, revenue]);

  // Sort: by revenue desc so top earner is visually obvious.
  const ranked = useMemo(() => {
    if (!tools) return [];
    return tools
      .map((t, i) => ({ t, rev: revenue[i]?.data }))
      .sort((a, b) => (b.rev?.total_cents ?? 0) - (a.rev?.total_cents ?? 0));
  }, [tools, revenue]);

  if (error) {
    return <div className="text-destructive text-sm">Error loading tools: {String(error)}</div>;
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Tool Factory · Tools"
        title="Tools"
        subtitle="Live and draft tools across your portfolio. Click a row for config, monetization routes, and events."
        actions={
          <>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-transparent px-4 py-[9px] font-heading text-[12px] font-semibold tracking-[0.04em] text-foreground transition-colors duration-150 hover:bg-secondary/60"
            >
              Filters
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-[9px] font-heading text-[12px] font-semibold tracking-[0.04em] text-background transition-colors duration-150 hover:bg-gold/90"
            >
              + New tool
            </button>
          </>
        }
      />

      <div className="mb-8 grid grid-cols-1 gap-[14px] md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Total tools"
          value={stats.total}
          delta={`${stats.live} live · ${stats.draft} draft · ${stats.paused} paused`}
        />
        <StatTile label="Leads · 7d" value={0} delta="No leads yet" />
        <StatTile
          label="Revenue · 30d"
          accent="gold"
          value={formatCents(stats.revenueTotal)}
          delta={
            Object.entries(stats.bySource)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => `${k} ${formatCents(v)}`)
              .join(' · ') || '—'
          }
        />
        <StatTile
          label="Active tools"
          value={
            <>
              {stats.live}
              <span className="text-foreground/40 text-[18px]"> / {stats.total}</span>
            </>
          }
          delta={`${Math.max(stats.total - stats.live, 0)} idle`}
        />
      </div>

      <SectionHead title="All tools" meta="Live data" />

      <TableWrap>
        <thead>
          <tr>
            <Th>Slug</Th>
            <Th>Name</Th>
            <Th>Kind</Th>
            <Th>Status</Th>
            <Th align="right">Revenue (all-time)</Th>
            <Th>By source</Th>
          </tr>
        </thead>
        <tbody>
          {ranked.length === 0 ? (
            <Tr hoverable={false}>
              <Td>
                <span className="text-foreground/50">No tools yet.</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
              <Td align="right">
                <span className="text-foreground/30 font-mono">$0</span>
              </Td>
              <Td>
                <span className="text-foreground/30">—</span>
              </Td>
            </Tr>
          ) : (
            ranked.map(({ t, rev }, idx) => {
              const sources = rev
                ? Object.entries(rev.by_source)
                    .filter(([, v]) => v > 0)
                    .sort((a, b) => b[1] - a[1])
                : [];
              const isTop = idx === 0 && (rev?.total_cents ?? 0) > 0;
              const isZero = !rev || rev.total_cents === 0;
              return (
                <Tr key={t.id}>
                  <Td>
                    <Link
                      to={`/tools/${t.slug}`}
                      className="font-mono text-[12px] text-foreground/85 hover:text-gold transition-colors"
                    >
                      {t.slug}
                    </Link>
                  </Td>
                  <Td>
                    <span className="font-medium text-[13.5px]">{t.name}</span>
                  </Td>
                  <Td>
                    <Pill>{t.kind}</Pill>
                  </Td>
                  <Td>
                    <StatusLabel status={t.status} />
                  </Td>
                  <Td align="right">
                    <span
                      className={[
                        'font-mono text-[13px]',
                        isZero
                          ? 'font-normal text-foreground/35'
                          : isTop
                            ? 'font-semibold text-gold'
                            : 'font-semibold',
                      ].join(' ')}
                    >
                      {formatCents(rev?.total_cents ?? 0)}
                    </span>
                  </Td>
                  <Td>
                    {sources.length === 0 ? (
                      <span className="font-mono text-[11px] text-foreground/40">—</span>
                    ) : (
                      <span className="font-mono text-[11px] text-foreground/65">
                        {sources.map(([k]) => k).join(' · ')}
                      </span>
                    )}
                  </Td>
                </Tr>
              );
            })
          )}
        </tbody>
      </TableWrap>

      <StatusBar
        left={`${stats.total} tools · ${Object.keys(stats.bySource).length} sources`}
        right="tools.holdengr.com"
      />
    </div>
  );
}
